import {
  getDiscipline,
  isDisciplineSlug,
  listDisciplines,
} from "@shared/labs/disciplines/registry";
import type { DisciplineSpec } from "@shared/labs/disciplines/types";
import { useCallback, useMemo, useState } from "react";

/**
 * Slug state for the candidate-intelligence form. The picker in the form is
 * driven by the live `frameworks` list (DB-backed scoring_configs rows) — so
 * a valid slug may not match a registered `DisciplineSpec` yet. Callers that
 * need to know whether the active slug resolves to a runtime spec read
 * `spec` (undefined when no entry exists).
 *
 * The hook accepts any string in `setSlug`. Validation belongs at the URL
 * boundary or wherever stale data enters — the `isValidRegistrySlug` export
 * is the same guard the registry uses. Pre-validating before hydrating from
 * a URL param keeps a stale `?discipline=xyz` from poisoning state.
 *
 * The empty-string state is treated as "unselected" (matches the form's
 * placeholder behavior). `spec` is undefined when slug is empty.
 */
export function useDiscipline(initial?: string) {
  const [slug, setSlug] = useState<string>(initial ?? "");
  const spec: DisciplineSpec | undefined = useMemo(
    () => (slug ? getDiscipline(slug) : undefined),
    [slug],
  );
  const options = useMemo(() => listDisciplines(), []);
  const setSlugSafe = useCallback((next: string) => setSlug(next), []);
  return { slug, spec, setSlug: setSlugSafe, options };
}

/** Re-export so URL/query-param hydration can validate before calling
 *  `setSlug`. Keeps the hook itself permissive while giving boundary code a
 *  one-stop validator. */
export { isDisciplineSlug as isValidRegistrySlug };
